import { useState, useEffect } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useBetonMatieres, useReplaceBom } from '@/hooks/useBetonCatalog';
import type { ProduitBOMItem } from '@/types';
import { formatFCFA } from '@/utils/currency';
import { extractError } from '@/api/client';

interface Props {
  produitId:    string;
  initialItems: ProduitBOMItem[];
}

interface Row {
  matierePremiereId: string;
  quantite:          string;
  notes:             string;
}

const toRows = (items: ProduitBOMItem[]): Row[] =>
  items.map((i) => ({
    matierePremiereId: i.matierePremiereId,
    quantite:          i.quantite,
    notes:             i.notes ?? '',
  }));

export const BomEditor = ({ produitId, initialItems }: Props) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: mpList } = useBetonMatieres({ actif: true, limit: 200 });
  const replaceBom = useReplaceBom();

  useEffect(() => {
    setRows(toRows(initialItems));
    setDirty(false);
  }, [initialItems]);

  const updateRow = (index: number, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const addRow = () => {
    setRows((prev) => [...prev, { matierePremiereId: '', quantite: '0', notes: '' }]);
    setDirty(true);
  };

  const removeRow = (index: number) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const onSave = async () => {
    setError(null);

    // Validation : tous les IDs MP doivent être renseignés et uniques
    const ids = rows.map((r) => r.matierePremiereId).filter(Boolean);
    if (ids.length !== rows.length) {
      setError('Sélectionnez une matière première pour chaque ligne');
      return;
    }
    if (new Set(ids).size !== ids.length) {
      setError('Une matière première ne peut apparaître qu\'une seule fois');
      return;
    }

    const items = rows.map((r) => ({
      matierePremiereId: r.matierePremiereId,
      quantite:          parseFloat(r.quantite || '0'),
      notes:             r.notes || undefined,
    }));

    try {
      await replaceBom.mutateAsync({ id: produitId, items });
      setDirty(false);
    } catch (err) {
      setError(extractError(err).message);
    }
  };

  const matieresIndex = new Map((mpList?.data ?? []).map((m) => [m.id, m]));

  // Total coût de revient en preview live
  let totalCents = 0;
  for (const row of rows) {
    const mp = matieresIndex.get(row.matierePremiereId);
    if (mp) {
      totalCents += parseFloat(row.quantite || '0') * Number(BigInt(mp.prixUnitaireCents));
    }
  }

  return (
    <div className="space-y-3">
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</div>
      )}

      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left font-medium">Matière première</th>
              <th className="px-3 py-2 text-right font-medium">Quantité</th>
              <th className="px-3 py-2 text-left font-medium">Unité</th>
              <th className="px-3 py-2 text-right font-medium">Coût ligne</th>
              <th className="px-3 py-2 text-left font-medium">Notes</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-400">
                  Aucune matière première dans la nomenclature. Cliquez "Ajouter une ligne".
                </td>
              </tr>
            )}
            {rows.map((row, idx) => {
              const mp = matieresIndex.get(row.matierePremiereId);
              const ligneCents = mp ? parseFloat(row.quantite || '0') * Number(BigInt(mp.prixUnitaireCents)) : 0;
              return (
                <tr key={idx}>
                  <td className="px-3 py-2">
                    <select
                      value={row.matierePremiereId}
                      onChange={(e) => updateRow(idx, { matierePremiereId: e.target.value })}
                      className="h-9 w-full rounded-md border border-gray-200 bg-white px-2 text-sm"
                    >
                      <option value="">— Sélectionner —</option>
                      {(mpList?.data ?? []).map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.code} · {m.nom}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      type="number" step="0.0001" min="0"
                      value={row.quantite}
                      onChange={(e) => updateRow(idx, { quantite: e.target.value })}
                      className="h-9 text-right tabular-nums"
                    />
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-500">{mp?.unite ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-xs text-gray-700">
                    {formatFCFA(Math.round(ligneCents))}
                  </td>
                  <td className="px-3 py-2">
                    <Input
                      value={row.notes}
                      onChange={(e) => updateRow(idx, { notes: e.target.value })}
                      className="h-9"
                      placeholder="optionnel"
                    />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => removeRow(idx)}
                      className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      aria-label="Supprimer la ligne"
                    >
                      <Trash2 className="size-4" />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={3} className="px-3 py-2 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Coût de revient unitaire
                </td>
                <td className="px-3 py-2 text-right text-sm font-semibold tabular-nums text-gray-900">
                  {formatFCFA(Math.round(totalCents))}
                </td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="flex items-center justify-between">
        <Button type="button" variant="secondary" size="sm" onClick={addRow}>
          <Plus className="size-4" /> Ajouter une ligne
        </Button>
        <Button type="button" disabled={!dirty} loading={replaceBom.isPending} onClick={onSave}>
          <Save className="size-4" /> Enregistrer le BOM
        </Button>
      </div>
    </div>
  );
};
